import { trackPayClick, trackStartStep } from "@/lib/analytics";
import { useEffect, useRef, useState, type MouseEvent } from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useChildMatches,
} from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";

import {
  captureAbandonReason,
  getSessionByToken,
  getSessionLanguage,
  type PretestSessionDTO,
} from "@/lib/sessions.functions";
import type { LanguageKey } from "@/lib/yolutap/types";
import {
  PretestIntroScreen,
  pretestSeenKey,
  pretestSetupKeyFor,
} from "@/components/pretest/PretestIntroScreen";
import { PRICING_AZN, isKnownTier } from "@/lib/payment/pricing";
import { PROMO_ERROR_PARAM, mustCheckPromoBeforePay, promoErrorFromSearch } from "@/lib/payment/promo-ui";
import { RETURN_TOKEN_STORAGE_KEY } from "@/lib/payment/return-storage";
import { trackPurchase, oncePerSession } from "@/lib/analytics";
import { isAccountLocale, type AccountLocale } from "@/lib/locale";
import { usePublishPageLanguage } from "@/lib/page-language";

// Abandoned-checkout reason-link target. The email sends parents to
// /session/<token>?reason=price|unsure|payment|later — we read the
// query, record it (server-side, first-click wins), then strip the
// param from the URL so a refresh doesn't re-log and the parent ends
// up on PayScreen for a second pay attempt.
const ABANDON_REASONS = ["price", "unsure", "payment", "later"] as const;
type AbandonReason = (typeof ABANDON_REASONS)[number];

export const Route = createFileRoute("/session/$token")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { reason?: AbandonReason; lang?: AccountLocale } => ({
    reason:
      typeof search.reason === "string" &&
      (ABANDON_REASONS as readonly string[]).includes(search.reason)
        ? (search.reason as AbandonReason)
        : undefined,
    // Display language requested by whoever opened the link. The cabinet sets
    // it to the ACCOUNT's language so a report opened from there is re-rendered
    // in the language the person actually uses; an anonymous visit carries no
    // ?lang and keeps the language the test was taken in. Only the report page
    // honours it — the intro, the test itself and the PDF stay bound to
    // language_key, because those ARE the test.
    lang: isAccountLocale(search.lang) ? search.lang : undefined,
  }),
  // Язык сессии — для <html lang> уже в серверном HTML, у этой страницы и у
  // /test, /result, /print под ней (RootShell → routePageLanguage). Без него
  // азербайджанский тест уходил с lang="ru", и переводчик Safari «переводил»
  // его с русского, ломая варианты ответа (19.10.2026). Язык сессии не
  // меняется — второй раз за ним не ходим.
  loader: async ({ params }) => ({
    pageLang: await getSessionLanguage({ data: { access_token: params.token } }).catch(() => null),
  }),
  staleTime: Infinity,
  head: () => ({
    meta: [{ title: "Session — Yolutap" }, { name: "robots", content: "noindex" }],
  }),
  component: SessionLayout,
});

// ─── Palette & constants ──────────────────────────────────────────────────────

const P = {
  teal: "#1E5A6B",
  tealInk: "#143F4B",
  amber: "#F0A63A",
  ink: "#0F2A38",
  muted: "#6B8090",
  line: "#E3E7EB",
  paper: "#FBFAF7",
  cream: "#F4F1EA",
  cream2: "#EDE7DA",
  bg: "#F0EDE5",
} as const;

const FONT = "'Nunito Sans', system-ui, sans-serif";
const SERIF = "'Source Serif 4', 'Georgia', serif";

// ─── Translations ─────────────────────────────────────────────────────────────

const T = {
  ru: {
    loading: "Загрузка...",
    notFound: "Сессия не найдена.",
    createNew: "Начать новую",
    payTitle: "Активировать доступ",
    payAmount: "Сумма",
    payReturnHint:
      "После оплаты вернитесь на сайт кнопкой возврата внизу страницы банка. Ссылка на диагностику придёт и на почту.",
    promoLabel: "Промокод",
    promoPlaceholder: "Введите код",
    promoApply: "Применить",
    promoChecking: "Проверяем…",
    promoRemove: "Убрать",
    promoApplied: "Код применён",
    promoWas: "Было",
    promoAttribution: (price: string) => `Код принят. Цена не меняется — ${price}.`,
    promoErr: {
      empty: "Введите код.",
      malformed: "Такого кода не бывает — только буквы и цифры.",
      not_found: "Код не найден.",
      inactive: "Код отключён.",
      not_started: "Код ещё не действует.",
      expired: "Срок кода истёк.",
      exhausted: "Этот код уже использован.",
      wrong_tier: "Код не действует на этот тариф.",
      unavailable: "Не удалось проверить код. Попробуйте ещё раз.",
    },
    payBtn: "Перейти к оплате",
    payBusy: "Перенаправляем на AzeriCard...",
    payUnavailable: "Оплата временно недоступна.",
    payUnavailableHint: "Попробуйте позже.",
    payCheckingAvailability: "Проверяем доступность оплаты...",
    startBtn: "Начать диагностику",
    continueBtn: "Продолжить",
    paymentConfirmed: "Оплата подтверждена — диагностика готова к запуску",
    completeTitle: "Диагностика завершена",
    completeText: "Ваш отчёт готов.",
    openReport: "Открыть отчёт",
    questions: "вопросов",
    blocks: "блоков",
    method: "методика",
    forName: "Для",
    ageLabel: "лет",
    backHome: "На главную",
  },
  az: {
    loading: "Yüklənir...",
    notFound: "Sessiya tapılmadı.",
    createNew: "Yeni başla",
    payTitle: "Girişi aktivləşdir",
    payAmount: "Məbləğ",
    payReturnHint:
      "Ödənişdən sonra bank səhifəsinin aşağısındakı qayıdış düyməsi ilə sayta qayıdın. Diaqnostikanın keçidi e-poçtunuza da gələcək.",
    promoLabel: "Promokod",
    promoPlaceholder: "Kodu daxil edin",
    promoApply: "Tətbiq et",
    promoChecking: "Yoxlanılır…",
    promoRemove: "Ləğv et",
    promoApplied: "Kod tətbiq edildi",
    promoWas: "Əvvəl",
    promoAttribution: (price: string) => `Kod qəbul edildi. Qiymət dəyişmir — ${price}.`,
    promoErr: {
      empty: "Kodu daxil edin.",
      malformed: "Belə kod olmur — yalnız hərf və rəqəm.",
      not_found: "Kod tapılmadı.",
      inactive: "Kod söndürülüb.",
      not_started: "Kod hələ qüvvədə deyil.",
      expired: "Kodun müddəti bitib.",
      exhausted: "Bu kod artıq istifadə olunub.",
      wrong_tier: "Kod bu tarifə şamil olunmur.",
      unavailable: "Kodu yoxlamaq alınmadı. Yenidən cəhd edin.",
    },
    payBtn: "Ödənişə keç",
    payBusy: "AzeriCard-a yönləndirilir...",
    payUnavailable: "Ödəniş müvəqqəti əlçatmazdır.",
    payUnavailableHint: "Daha sonra yenidən cəhd edin.",
    payCheckingAvailability: "Ödəniş əlçatanlığı yoxlanılır...",
    startBtn: "Diaqnostikaya başla",
    continueBtn: "Davam et",
    paymentConfirmed: "Ödəniş təsdiqləndi — diaqnostika başlamağa hazırdır",
    completeTitle: "Diaqnostika tamamlandı",
    completeText: "Hesabatınız hazırdır.",
    openReport: "Hesabatı aç",
    questions: "sual",
    blocks: "blok",
    method: "metodika",
    forName: "Üçün",
    ageLabel: "yaş",
    backHome: "Ana səhifəyə",
  },
  en: {
    loading: "Loading...",
    notFound: "Session not found.",
    createNew: "Start new",
    payTitle: "Activate access",
    payAmount: "Amount",
    payReturnHint:
      "After paying, return to the site using the back button at the bottom of the bank's page. The link to the assessment will also arrive by email.",
    promoLabel: "Promo code",
    promoPlaceholder: "Enter code",
    promoApply: "Apply",
    promoChecking: "Checking…",
    promoRemove: "Remove",
    promoApplied: "Code applied",
    promoWas: "Was",
    promoAttribution: (price: string) => `Code accepted. The price stays the same — ${price}.`,
    promoErr: {
      empty: "Enter a code.",
      malformed: "That is not a code — letters and digits only.",
      not_found: "Code not found.",
      inactive: "This code is switched off.",
      not_started: "This code is not active yet.",
      expired: "This code has expired.",
      exhausted: "This code has already been used.",
      wrong_tier: "This code does not apply to this package.",
      unavailable: "Could not check the code. Please try again.",
    },
    payBtn: "Continue to payment",
    payBusy: "Redirecting to AzeriCard...",
    payUnavailable: "Payment temporarily unavailable.",
    payUnavailableHint: "Please try again later.",
    payCheckingAvailability: "Checking payment availability...",
    startBtn: "Start assessment",
    continueBtn: "Continue",
    paymentConfirmed: "Payment confirmed — your assessment is ready to begin",
    completeTitle: "Assessment complete",
    completeText: "Your report is ready.",
    openReport: "Open report",
    questions: "questions",
    blocks: "blocks",
    method: "method",
    forName: "For",
    ageLabel: "years",
    backHome: "Back to home",
  },
} as const;

// ─── Age group intro data ─────────────────────────────────────────────────────

const INTRO: Record<
  string,
  {
    title: Record<LanguageKey, string>;
    lede: Record<LanguageKey, string>;
    q: number;
    blocksCount: number;
    methodLabel: Record<LanguageKey, string>;
    time: string;
  }
> = {
  "10_11": {
    title: {
      ru: "Диагностика 10–11 лет",
      az: "10–11 yaş diaqnostikası",
      en: "Assessment: 10–11 Years",
    },
    lede: {
      ru: "Игровой формат · образы, сильные стороны и среда",
      az: "Oyun formatı · obrazlar, güclü tərəflər və mühit",
      en: "Playful format · images, strengths and environment",
    },
    q: 60,
    blocksCount: 3,
    time: "~25",
    methodLabel: {
      ru: "Архетипы + Среда",
      az: "Arxetiplər + Mühit",
      en: "Archetypes + Environment",
    },
  },
  "12_13": {
    title: {
      ru: "Диагностика 12–13 лет",
      az: "12–13 yaş diaqnostikası",
      en: "Assessment: 12–13 Years",
    },
    lede: {
      ru: "Первые сильные предпочтения · архетипы и направления",
      az: "İlk güclü üstünlüklər · arxetiplər və istiqamətlər",
      en: "First strong preferences · archetypes and directions",
    },
    q: 48,
    blocksCount: 4,
    time: "~20",
    methodLabel: { ru: "Архетипы + RIASEC", az: "Arxetiplər + RIASEC", en: "Archetypes + RIASEC" },
  },
  "14_15": {
    title: {
      ru: "Диагностика 14–15 лет",
      az: "14–15 yaş diaqnostikası",
      en: "Assessment: 14–15 Years",
    },
    lede: {
      ru: "Ситуативный формат A/B/C/D · архетип, направления, среда",
      az: "Situativ A/B/C/D formatı · arxetip, istiqamətlər, mühit",
      en: "Situational A/B/C/D · archetype, directions, environment",
    },
    q: 48,
    blocksCount: 3,
    time: "~20",
    methodLabel: { ru: "A/B/C/D ситуации", az: "A/B/C/D situasiyalar", en: "A/B/C/D situations" },
  },
  "16_18": {
    title: {
      ru: "Диагностика 16–18 лет",
      az: "16–18 yaş diaqnostikası",
      en: "Assessment: 16–18 Years",
    },
    lede: {
      ru: "Взрослые ситуации A/B/C/D · архетип, карьерный формат, реальный опыт",
      az: "Böyüklərə aid A/B/C/D situasiyalar · arxetip, karyera formatı, real təcrübə",
      en: "Adult A/B/C/D situations · archetype, career format, real experience",
    },
    q: 48,
    blocksCount: 4,
    time: "~20",
    methodLabel: { ru: "A/B/C/D ситуации", az: "A/B/C/D situasiyalar", en: "A/B/C/D situations" },
  },
  // 18_22 arrived via /session/<token> after the Path B integration but
  // the INTRO dictionary was never extended — sessions with
  // age_group='18_22' fell through to INTRO_FALLBACK below and rendered
  // "Диагностика 14–15 лет". Numbers align with reports1822.ts pipeline:
  // 72 questions (24 RIASEC + 16 values + 14 style + 8 skills + 4 context
  //               + 6 scale), 6 blocks, ~30 min based on Path B pacing.
  // RU copy approved by product owner 2026-07-02.
  // AZ/EN copy landed 2026-09-05, азербайджанский проверен владельцем.
  // До этого все три языка указывали на одни и те же РУССКИЕ строки —
  // азербайджанец и англичанин видели русский заголовок на первом экране
  // после оплаты. Заглушка была сознательной и подписана TODO(i18n), но
  // прожила дольше, чем предполагалась: к моменту замены через неё прошли
  // 20 сессий из 26 в этой группе.
  //
  // Английский заголовок оставляет «Years» вслед за соседними группами
  // («Assessment: 16–18 Years»), хотя русский своё «лет» потерял. Решение
  // владельца: английский равняется на английских соседей, а не на русский.
  // Рассогласование русского с соседями записано в бэклог.
  "18_22": {
    title: {
      ru: "Диагностика 18–22",
      az: "18–22 yaş diaqnostikası",
      en: "Assessment: 18–22 Years",
    },
    lede: {
      ru: "Взрослый формат · RIASEC, ценности, стиль работы",
      az: "Böyüklər üçün format · RIASEC, dəyərlər, iş üslubu",
      en: "Adult format · RIASEC, values, work style",
    },
    q: 72,
    blocksCount: 6,
    time: "~30",
    methodLabel: {
      ru: "RIASEC + ценности + стиль",
      az: "RIASEC + dəyərlər + üslub",
      en: "RIASEC + values + style",
    },
  },
};

// Neutral fallback for any age_group we forgot to add above. Historically
// this pointed at INTRO["14_15"], which is what created the 18_22 → shown
// as 14-15 bug — a missing key silently painted a specific age on top of
// the wrong session. Numeric fields keep 14_15's shape (harmless as-is;
// the fallback is a defensive net, not a display target) but the `title`
// is neutralised so a future missing key surfaces as generic "Диагностика"
// rather than lying about the age band.
const INTRO_FALLBACK: (typeof INTRO)["14_15"] = {
  ...INTRO["14_15"],
  title: {
    ru: "Диагностика",
    az: "Diaqnostika",
    en: "Assessment",
  },
};

// ─── Wordmark ─────────────────────────────────────────────────────────────────

function Wordmark() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 0,
        fontFamily: FONT,
        fontWeight: 800,
        fontSize: 22,
        color: P.tealInk,
        letterSpacing: "-0.5px",
      }}
    >
      <span>Y</span>
      <span style={{ position: "relative", display: "inline-block" }}>
        <span>o</span>
        <span
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -70%)",
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: P.amber,
            display: "block",
          }}
        />
      </span>
      <span>lutap</span>
    </div>
  );
}

// ─── Parent setup gate (shown before IntroScreen for child bands) ────────────
//
// Per docs/yolutap-pretest-intro-screens-CC.md screen ④:
// for 10_11 / 12_13 / 14_15 the parent reads a short setup instruction
// before clicking "Start" → handing the device to the child. Shown once
// per (token × screen) via localStorage. After ack, the existing
// IntroScreen renders unchanged. 16_18 / 18_22 bypass the gate.
// 14_15 is on the list because the current /start flow is still
// parent-driven (parent_email + consent on the form); the doc says
// 16_18 deliberately skips even though the form is also parent-driven —
// the teen reads ② themselves on /session/<token>/test.

function IntroScreenWithParentSetup({
  session,
  onStart,
  isContinue,
}: {
  session: PretestSessionDTO;
  onStart: () => void;
  isContinue: boolean;
}) {
  const lang = (session.language_key ?? "ru") as LanguageKey;
  // Один экран или другой, но «увиденность» помнится ОТДЕЛЬНО по каждому:
  // ключ хранилища собран из самого screenKey. Иначе ребёнок, которому
  // сначала показали родительский вариант, не увидел бы детский после того,
  // как появится азербайджанский текст.
  const setupKey = pretestSetupKeyFor({
    ageGroup: session.age_group,
    schoolSeat: !!session.school_id,
    lang,
  });
  const [setupAck, setSetupAck] = useState<boolean>(false);

  useEffect(() => {
    if (!setupKey || typeof window === "undefined") return;
    const lsVal = window.localStorage.getItem(pretestSeenKey(session.access_token, setupKey));
    setSetupAck(!!lsVal);
  }, [session.access_token, setupKey]);

  if (setupKey && !setupAck) {
    return (
      <PretestIntroScreen
        screenKey={setupKey}
        lang={lang}
        onAck={() => {
          if (typeof window !== "undefined") {
            window.localStorage.setItem(pretestSeenKey(session.access_token, setupKey), "1");
          }
          setSetupAck(true);
        }}
      />
    );
  }
  return <IntroScreen session={session} onStart={onStart} isContinue={isContinue} />;
}

// ─── Intro screen (shown before test start) ───────────────────────────────────

function IntroScreen({
  session,
  onStart,
  isContinue,
}: {
  session: PretestSessionDTO;
  onStart: () => void;
  isContinue: boolean;
}) {
  const lang = (session.language_key ?? "ru") as LanguageKey;
  const t = T[lang] ?? T.ru;
  const intro = INTRO[session.age_group] ?? INTRO_FALLBACK;

  const stats = [
    { value: String(intro.q), label: t.questions },
    { value: String(intro.blocksCount), label: t.blocks },
    { value: intro.methodLabel[lang], label: t.method },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: P.bg,
        backgroundImage:
          "radial-gradient(rgba(20,63,75,.04) 1px,transparent 1px),radial-gradient(rgba(20,63,75,.02) 1px,transparent 1px)",
        backgroundSize: "28px 28px,56px 56px",
        backgroundPosition: "0 0,14px 14px",
        fontFamily: FONT,
        color: P.ink,
      }}
    >
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "48px 24px 64px" }}>
        {/* Top bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 40,
          }}
        >
          <Wordmark />
          <a
            href="/"
            style={{ fontSize: 13, fontWeight: 600, color: P.muted, textDecoration: "none" }}
          >
            {t.backHome}
          </a>
        </div>

        {/*
          Post-payment confirmation banner. Shown when the session is
          paid AND the user hasn't started answering yet — a landing
          right after AzeriCard 3DS success is the exact case where a
          parent complained "нет автоперехода, приходится нажимать назад".
          The IntroScreen (and its parent-setup gate) is an intentional
          pause before test start; without an explicit "your payment
          went through" cue it can read as a limbo page and the parent
          bails. isContinue=true means the child already answered at
          least one question, so this is a return visit — hide the
          banner there to avoid nagging a mid-flow user with stale
          receipt-style copy.
        */}
        {session.status === "paid" && !isContinue && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "#EAF6EF",
              border: "1px solid #B7DEC5",
              color: "#1E6A44",
              borderRadius: 12,
              padding: "12px 16px",
              marginBottom: 24,
              fontSize: 14,
              fontWeight: 600,
              lineHeight: 1.4,
            }}
            role="status"
            aria-live="polite"
          >
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 22,
                height: 22,
                borderRadius: "50%",
                background: "#2F8A6A",
                color: "#fff",
                fontSize: 14,
                fontWeight: 800,
                flexShrink: 0,
              }}
            >
              ✓
            </span>
            <span>{t.paymentConfirmed}</span>
          </div>
        )}

        {/* Title */}
        <h1
          style={{
            fontFamily: SERIF,
            fontSize: "clamp(26px,4vw,36px)",
            fontWeight: 700,
            color: P.tealInk,
            lineHeight: 1.2,
            marginBottom: 10,
          }}
        >
          {intro.title[lang]}
        </h1>

        {/* Child info */}
        <p style={{ fontSize: 15, color: P.muted, marginBottom: 8, fontWeight: 600 }}>
          {t.forName}: <span style={{ color: P.ink }}>{session.child_name}</span>
        </p>

        {/* Lede */}
        <p style={{ fontSize: 15, color: P.muted, marginBottom: 36, lineHeight: 1.6 }}>
          {intro.lede[lang]}
        </p>

        {/* Stats */}
        <div style={{ display: "flex", gap: 14, marginBottom: 48, flexWrap: "wrap" }}>
          {stats.map((s) => (
            <div
              key={s.label}
              style={{
                background: P.paper,
                border: `1px solid ${P.line}`,
                borderRadius: 14,
                padding: "20px 22px",
                flex: "1 1 140px",
                minWidth: 120,
              }}
            >
              <div style={{ fontSize: 20, fontWeight: 800, color: P.teal, marginBottom: 4 }}>
                {s.value}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: P.muted,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  fontWeight: 600,
                }}
              >
                {s.label}
              </div>
            </div>
          ))}
          <div
            style={{
              background: P.paper,
              border: `1px solid ${P.line}`,
              borderRadius: 14,
              padding: "20px 22px",
              flex: "1 1 140px",
              minWidth: 120,
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 800, color: P.teal, marginBottom: 4 }}>
              {intro.time}
            </div>
            <div
              style={{
                fontSize: 11,
                color: P.muted,
                textTransform: "uppercase",
                letterSpacing: "0.12em",
                fontWeight: 600,
              }}
            >
              {lang === "ru" ? "минут" : lang === "az" ? "dəqiqə" : "minutes"}
            </div>
          </div>
        </div>

        {/* CTA */}
        <button
          type="button"
          onClick={onStart}
          style={{
            background: P.teal,
            color: "#fff",
            border: "none",
            borderRadius: 14,
            padding: "18px 40px",
            fontSize: 16,
            fontWeight: 700,
            fontFamily: FONT,
            cursor: "pointer",
            boxShadow: "0 4px 20px rgba(30,90,107,.3)",
            transition: "background 0.15s",
            width: "100%",
            maxWidth: 360,
            display: "block",
          }}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.background = P.tealInk)
          }
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = P.teal)}
        >
          {isContinue ? t.continueBtn : t.startBtn} →
        </button>
      </div>
    </div>
  );
}

// ─── Payment screen ───────────────────────────────────────────────────────────
//
// Real-money AzeriCard payment flow. There is NO mock path here anymore —
// the previous mockPaySession stub was removed in feat/wire-frontend-paywall-azericard.
//
// Flow:
//   1. On mount, GET /api/payment/availability — fail-closed (anything but
//      {enabled:true} → render "оплата временно недоступна").
//   2. When enabled, render a hidden <form action="/api/payment/azericard-start"
//      method="POST"> with the session's access_token. Pay button is
//      type="submit" inside that form.
//   3. Native browser form submission → /api/payment/azericard-start
//      returns the auto-submit HTML page from azericard-start.ts → its
//      inline <script> POSTs straight to AzeriCard's gateway with the
//      signed P_SIGN fields. User lands on AzeriCard's hosted card page.
//
// AZERICARD_ENABLED=0 path: availability returns {enabled:false}, button
// is hidden, message is shown. There is NO fallback that grants free
// access; the session stays in pending_payment until ENABLED=1 + the user
// returns to retry.

function PayScreen({ session }: { session: PretestSessionDTO }) {
  const lang = (session.language_key ?? "ru") as LanguageKey;
  const t = T[lang] ?? T.ru;

  // Tier → amount lookup. Fail-closed when the session carries a tier
  // we don't price (e.g. legacy "plus" sessions from the v2.5 enum
  // default). PayScreen never displays a number it can't tie to the
  // backend signing path; the disabled UI uses the same message as
  // the gate-closed branch.
  const tier = isKnownTier(session.package_type) ? session.package_type : null;
  if (!tier && typeof window !== "undefined") {
    // Surfaced via console.error → Sentry beforeSend (PII scrub strips
    // any access_token / email; session_id is safe to ship).
    console.error("[PayScreen] unknown tier on session — refusing to render Pay button", {
      session_id: session.id,
      tier: session.package_type,
    });
  }
  const displayAmount = tier ? PRICING_AZN[tier].display : "";

  // payment_view — последний шаг воронки /start (28.10.2026, src/lib/start-funnel.ts):
  // экран оплаты показан. Один раз на сессию в этом браузере; без токена и почты.
  useEffect(() => {
    if (oncePerSession("payment_view:" + session.id)) trackStartStep("payment_view", { lang, pkg: session.package_type ?? "" });
  }, [session.id, lang, session.package_type]);

  // ── Промокод ──
  //
  // Экран показывает то, что ответил сервер, и отправляет в оплату СЛОВО,
  // а не сумму. Поля с суммой в форме нет вовсе, поэтому подменить в
  // браузере нечего: /azericard-start пересчитает цену сам, той же
  // функцией, что ответила этому экрану.
  const [promoInput, setPromoInput] = useState("");
  // attribution — код атрибуции (Teleperformance): цену не меняет, и экран
  // говорит об этом словами вместо зачёркнутой цены.
  const [promo, setPromo] = useState<{ code: string; amount: string; attribution: boolean } | null>(
    null,
  );
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);

  // true — код применён. Нужен «Оплатить», которое проверяет введённый, но не
  // применённый код вместо того, чтобы молча его потерять.
  async function applyPromo(): Promise<boolean> {
    if (promoBusy) return false;
    setPromoBusy(true);
    setPromoError(null);
    try {
      const r = await fetch("/api/payment/promo-check", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ access_token: session.access_token, code: promoInput }),
      });
      const data = r.ok ? await r.json() : null;
      if (data?.ok === true) {
        setPromo({
          code: String(data.code),
          amount: String(data.amount),
          attribution: data.attribution === true,
        });
        setPromoError(null);
        return true;
      }
      setPromo(null);
      const reason = String(data?.reason ?? "unavailable");
      // Причина из закрытого списка. Незнакомую трактуем как «не смогли
      // проверить», а не как «кода нет»: перепутать эти два ответа —
      // значит сказать человеку, что его код поддельный, когда у нас
      // просто упала база.
      setPromoError(t.promoErr[reason as keyof typeof t.promoErr] ?? t.promoErr.unavailable);
      return false;
    } catch {
      setPromo(null);
      setPromoError(t.promoErr.unavailable);
      return false;
    } finally {
      setPromoBusy(false);
    }
  }

  // Подпись платежа вернула на экран с причиной (?promo_error=…): код не
  // прошёл уже при оплате — погасили, пока человек был на странице. Причину
  // показываем, адрес чистим, чтобы перезагрузка её не повторяла.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reason = promoErrorFromSearch(window.location.search);
    if (!reason) return;
    setPromoError(t.promoErr[reason as keyof typeof t.promoErr] ?? t.promoErr.unavailable);
    const url = new URL(window.location.href);
    url.searchParams.delete(PROMO_ERROR_PARAM);
    window.history.replaceState(window.history.state, "", url.toString());
    // Только при открытии экрана.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function removePromo() {
    setPromo(null);
    setPromoInput("");
    setPromoError(null);
  }

  // «180.00» → «180 AZN», чтобы цена по коду выглядела так же, как
  // витринная, а не как строка из базы.
  const promoDisplay = promo ? `${promo.amount.replace(/\.00$/, "")} AZN` : "";
  // Цена, которую называет сообщение кода атрибуции: витрина этого тарифа.
  const listPriceLabel = tier ? `${PRICING_AZN[tier].amount.replace(/\.00$/, "")} ₼` : "";

  const [availability, setAvailability] = useState<
    "loading" | "enabled" | "disabled"
  >("loading");
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    // If tier is unknown, skip the availability probe entirely and
    // render the disabled UI directly — the gate's true state is
    // irrelevant here because we have no priced offer to make.
    if (!tier) {
      setAvailability("disabled");
      return;
    }
    fetch("/api/payment/availability", {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : { enabled: false }))
      .then((data) => {
        if (cancelled) return;
        setAvailability(data?.enabled === true ? "enabled" : "disabled");
      })
      .catch(() => {
        // Fail-closed on any network error. No mock fallback.
        if (!cancelled) setAvailability("disabled");
      });
    return () => {
      cancelled = true;
    };
  }, [tier]);

  function handlePay(e: MouseEvent<HTMLButtonElement>) {
    if (submitting) return;
    // Код введён, но не применён: оплату не начинаем, сначала проверяем его.
    // Годен — человек видит новую цену и нажимает ещё раз. Не годен — видит
    // причину и может убрать код одним нажатием. Раньше такой код молча
    // терялся, и оплата шла по полной цене без него (живая проверка 17.09).
    if (mustCheckPromoBeforePay(promo !== null, promoInput)) {
      e.preventDefault();
      void applyPromo();
      return;
    }
    setSubmitting(true);
    // Save the session's access_token to localStorage right BEFORE the
    // browser redirects to AzeriCard. After payment AzeriCard bounces
    // the user to MERCH_URL (the fixed merchant return URL, registered
    // with the bank panel — single value, no per-transaction return).
    // The /payment-return route reads this key and navigates the user
    // back to their session. The actual paid/unpaid status is set by
    // the server-to-server callback at /api/payment/azericard-callback;
    // localStorage is UX-only — never the source of truth.
    //
    // Wrapped in try-catch: Safari private mode + storage quota errors
    // are real. Failure here doesn't block payment — the user just hits
    // the soft fallback on /payment-return (paste-token / new-session /
    // contact-support).
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(
          RETURN_TOKEN_STORAGE_KEY,
          session.access_token,
        );
      }
    } catch {
      // Storage unavailable; continue with payment regardless.
    }
    // Native form submission. Browser POSTs to /api/payment/azericard-start,
    // receives the AzeriCard auto-submit HTML, runs its inline script, and
    // navigates to mpi.3dsecure.az. No JS-side parsing of the HTML response.
    // Последнее, что мы видим до того, как человек уходит на страницу банка.
    // Дальше начинается чужая территория: вернётся он или нет, мы узнаём
    // только из callback.
    trackPayClick();
    formRef.current?.submit();
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: P.bg,
        fontFamily: FONT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <div
        style={{
          background: P.paper,
          border: `1px solid ${P.line}`,
          borderRadius: 20,
          padding: "40px 36px",
          maxWidth: 420,
          width: "100%",
        }}
      >
        <Wordmark />
        <h2
          style={{
            fontFamily: SERIF,
            fontSize: 24,
            fontWeight: 700,
            color: P.tealInk,
            marginTop: 24,
            marginBottom: 8,
          }}
        >
          {t.payTitle}
        </h2>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: 14,
            color: P.muted,
            marginBottom: 24,
          }}
        >
          <span>{t.payAmount}</span>
          <span style={{ fontWeight: 700, color: P.ink }}>
            {promo && !promo.attribution ? (
              <>
                {/* Зачёркнутая витринная цена стоит РЯДОМ с новой, а не
                    вместо неё: человек должен видеть, что код сработал,
                    не сверяя число с тем, что помнит. */}
                <span
                  style={{
                    textDecoration: "line-through",
                    fontWeight: 400,
                    color: P.muted,
                    marginRight: 8,
                  }}
                >
                  {displayAmount}
                </span>
                {promoDisplay}
              </>
            ) : (
              displayAmount
            )}
          </span>
        </div>

        {/* Ввод кода показывается только там, где есть что оплачивать.
            На закрытом гейте и на неизвестном тарифе поля нет — оно
            обещало бы пересчёт, которого не будет. */}
        {availability === "enabled" && tier && (
          <div style={{ marginBottom: 24 }}>
            {promo ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  background: "#F0F7F5",
                  border: "1px solid #BFD9D2",
                  borderRadius: 12,
                  padding: "12px 14px",
                }}
              >
                <span style={{ fontSize: 13, color: P.tealInk }}>
                  {/* Код атрибуции цену не меняет — вместо зачёркнутой цены
                      человек видит, что код принят и сколько он заплатит. */}
                  {promo.attribution ? (
                    <>
                      {t.promoAttribution(listPriceLabel)}
                      <br />
                      <strong>{promo.code}</strong>
                    </>
                  ) : (
                    <>
                      {t.promoApplied}: <strong>{promo.code}</strong>
                    </>
                  )}
                </span>
                <button
                  type="button"
                  onClick={removePromo}
                  style={{
                    background: "none",
                    border: "none",
                    color: P.muted,
                    fontSize: 13,
                    fontFamily: FONT,
                    cursor: "pointer",
                    textDecoration: "underline",
                    minHeight: 44,
                    // Высота была 44, а ширина шла от длины слова: «Убрать»
                    // давало 41, азербайджанское «Ləğv et» — 27 на узком
                    // экране. Мишень меряется по обеим сторонам, поэтому
                    // минимум задан и по ширине, а отступы не дают тексту
                    // прилипнуть к её краю.
                    minWidth: 44,
                    padding: "0 8px",
                    flexShrink: 0,
                  }}
                >
                  {t.promoRemove}
                </button>
              </div>
            ) : (
              <>
                <label
                  htmlFor="promo"
                  style={{ display: "block", fontSize: 13, color: P.muted, marginBottom: 6 }}
                >
                  {t.promoLabel}
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    id="promo"
                    type="text"
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value)}
                    onKeyDown={(e) => {
                      // Enter внутри поля НЕ должен отправлять форму
                      // оплаты — иначе человек, дописавший код и нажавший
                      // Enter, уедет на шлюз по витринной цене.
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void applyPromo();
                      }
                    }}
                    placeholder={t.promoPlaceholder}
                    autoCapitalize="characters"
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={32}
                    style={{
                      flex: 1,
                      minHeight: 44,
                      borderRadius: 12,
                      border: `1px solid ${promoError ? "#D98A8A" : "#D8DEE3"}`,
                      padding: "10px 12px",
                      fontSize: 14,
                      fontFamily: FONT,
                      color: P.ink,
                      textTransform: "uppercase",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => void applyPromo()}
                    disabled={promoBusy || promoInput.trim().length === 0}
                    style={{
                      minHeight: 44,
                      borderRadius: 12,
                      border: `1px solid ${P.teal}`,
                      background: "transparent",
                      color: P.tealInk,
                      padding: "0 16px",
                      fontSize: 14,
                      fontWeight: 700,
                      fontFamily: FONT,
                      cursor: promoBusy ? "wait" : "pointer",
                      opacity: promoBusy || promoInput.trim().length === 0 ? 0.5 : 1,
                    }}
                  >
                    {promoBusy ? t.promoChecking : t.promoApply}
                  </button>
                </div>
                {promoError && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      margin: "8px 0 0",
                    }}
                  >
                    <p style={{ fontSize: 13, color: "#A34A4A", margin: 0 }}>{promoError}</p>
                    {/* Одним нажатием убрать код и оплатить без него: пока в
                        поле что-то введено, «Оплатить» сначала проверяет код. */}
                    <button
                      type="button"
                      onClick={removePromo}
                      style={{
                        background: "none",
                        border: "none",
                        color: P.muted,
                        fontSize: 13,
                        fontFamily: FONT,
                        cursor: "pointer",
                        textDecoration: "underline",
                        minHeight: 44,
                        minWidth: 44,
                        padding: "0 8px",
                        flexShrink: 0,
                      }}
                    >
                      {t.promoRemove}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {availability === "loading" && (
          <p style={{ fontSize: 13, color: P.muted, textAlign: "center", padding: "12px 0" }}>
            {t.payCheckingAvailability}
          </p>
        )}

        {availability === "disabled" && (
          <div
            style={{
              background: "#FFF8E6",
              border: "1px solid #E8D49A",
              borderRadius: 12,
              padding: "16px 18px",
              textAlign: "center",
            }}
          >
            <p style={{ fontSize: 14, fontWeight: 700, color: P.tealInk, marginBottom: 6 }}>
              {t.payUnavailable}
            </p>
            <p style={{ fontSize: 13, color: P.muted, margin: 0 }}>{t.payUnavailableHint}</p>
          </div>
        )}

        {availability === "enabled" && (
          <form
            ref={formRef}
            action="/api/payment/azericard-start"
            method="POST"
            // The encType matches what azericard-start.ts parses (it accepts
            // both JSON and form-encoded; form-encoded keeps the redirect
            // 100% browser-native, no JS-driven fetch involved).
            encType="application/x-www-form-urlencoded"
          >
            <input type="hidden" name="access_token" value={session.access_token} />
            {/* Слово, а не сумма. Единственное, что промокод передаёт в
                оплату: цену по нему сервер найдёт сам. */}
            {promo && <input type="hidden" name="promo_code" value={promo.code} />}
            {/* Режим кода: отказ по коду атрибуции при подписи не
                останавливает оплату. Скидки через это поле не получить. */}
            {promo && (
              <input
                type="hidden"
                name="promo_mode"
                value={promo.attribution ? "attribution" : "discount"}
              />
            )}
            <button
              type="submit"
              onClick={handlePay}
              disabled={submitting || promoBusy}
              style={{
                background: P.teal,
                color: "#fff",
                border: "none",
                borderRadius: 12,
                padding: "16px 32px",
                fontSize: 15,
                fontWeight: 700,
                fontFamily: FONT,
                cursor: submitting ? "wait" : "pointer",
                width: "100%",
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? t.payBusy : t.payBtn}
            </button>

            {/*
              ПРЕДУПРЕЖДЕНИЕ ДО РЕДИРЕКТА, А НЕ ПОСЛЕ.

              Страница банка после успешной оплаты предлагает «распечатать» и
              «вернуться» — и к диагностике ведёт второе, что неочевидно.
              Человек закрывает вкладку и не доходит до того, за что заплатил.
              Потеря тихая: деньги у нас, отчёт не открыт, жалоба уйдёт в банк.

              Строка стоит ПОД кнопкой намеренно: над ней она читалась бы как
              условие покупки, под — как то, что произойдёт дальше. Читают её
              ровно в момент, когда собираются нажать.

              Кнопка возврата НЕ названа цитатой. Её подпись зависит от языка
              страницы банка, а он приходит не от нашего сайта — проверить все
              варианты можно только новыми настоящими платежами. Неверная
              цитата хуже её отсутствия: человек будет искать слово, которого
              на экране нет.
            */}
            <p
              style={{
                fontSize: 12,
                color: P.muted,
                lineHeight: 1.5,
                margin: "10px 0 0",
                textAlign: "center",
              }}
            >
              {t.payReturnHint}
            </p>
          </form>
        )}

        {/*
          Успокаивающий блок. До него экран называл только сумму — на
          единственной странице, где родитель решает, тратить ли деньги. Две
          строки: что именно он покупает и возможность посмотреть сам предмет
          покупки. Образец отчёта уже лежал в /sample-report.pdf и был связан с
          лендингом и письмом о брошенной оплате, но не отсюда — не оттуда, где
          сомнение и возникает.

          Тексты — черновики владельца, вычитка носителя впереди. До этой
          правки здесь стояли заглушки TODO_*, и их видели платящие люди.
        */}
        <div style={{ marginTop: 18, borderTop: `1px solid ${P.line}`, paddingTop: 14 }}>
          <p style={{ fontSize: 13, color: P.muted, lineHeight: 1.5, margin: "0 0 8px" }}>
            {
              {
                ru: "Персональный отчёт на 10 страниц · разовый платёж, без подписки",
                az: "10 səhifəlik fərdi hesabat · birdəfəlik ödəniş, abunəlik yoxdur",
                en: "A personal 10-page report · one-time payment, no subscription",
              }[lang]
            }
          </p>
          <a
            href="/sample-report.pdf"
            target="_blank"
            rel="noopener"
            style={{ fontSize: 13, fontWeight: 700, color: P.teal, textDecoration: "underline" }}
          >
            {
              {
                ru: "Посмотреть образец отчёта (PDF)",
                az: "Nümunə hesabata baxın (PDF)",
                en: "See a sample report (PDF)",
              }[lang]
            }
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── Complete screen ──────────────────────────────────────────────────────────

function CompleteScreen({
  session,
  token,
  reportLang,
}: {
  session: PretestSessionDTO;
  token: string;
  // Display language requested for the REPORT (set by the cabinet). This
  // screen itself stays in the test's language — it belongs to the session —
  // but it must not swallow the request on the way through.
  reportLang?: AccountLocale;
}) {
  const lang = (session.language_key ?? "ru") as LanguageKey;
  const t = T[lang] ?? T.ru;
  const navigate = useNavigate();

  return (
    <div
      style={{
        minHeight: "100vh",
        background: P.bg,
        fontFamily: FONT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <Wordmark />
        <div style={{ marginTop: 40, marginBottom: 16, fontSize: 40 }}>✓</div>
        <h2
          style={{
            fontFamily: SERIF,
            fontSize: 26,
            fontWeight: 700,
            color: P.tealInk,
            marginBottom: 10,
          }}
        >
          {t.completeTitle}
        </h2>
        <p style={{ color: P.muted, marginBottom: 32 }}>{t.completeText}</p>
        <button
          type="button"
          onClick={() =>
            navigate({
              to: "/session/$token/result",
              params: { token },
              search: { lang: reportLang },
            })
          }
          style={{
            background: P.teal,
            color: "#fff",
            border: "none",
            borderRadius: 14,
            padding: "18px 40px",
            fontSize: 16,
            fontWeight: 700,
            fontFamily: FONT,
            cursor: "pointer",
            boxShadow: "0 4px 20px rgba(30,90,107,.3)",
          }}
        >
          {t.openReport} →
        </button>
      </div>
    </div>
  );
}

// ─── Route layout ─────────────────────────────────────────────────────────────

function SessionLayout() {
  const childMatches = useChildMatches();
  if (childMatches.length > 0) return <Outlet />;
  return <SessionPage />;
}

function SessionPage() {
  const { token } = Route.useParams();
  const { reason, lang: reportLang } = Route.useSearch();
  const navigate = useNavigate();
  const fetchSession = useServerFn(getSessionByToken);
  const recordReason = useServerFn(captureAbandonReason);

  // Reason-link from the abandoned-checkout email. Fire-and-forget POST
  // to the server (first-click wins inside the SQL), then immediately
  // strip ?reason= from the URL so a page refresh doesn't re-log and
  // a bookmark of the cleaned URL stays clean. The parent ends up on
  // /session/<token> with whatever screen status dictates — for a
  // pending_payment session that's PayScreen (second pay attempt),
  // which is the whole point of the email.
  //
  // Ref guard: in React 19 strict mode the effect runs twice on mount;
  // without the ref we'd POST twice. The server is idempotent anyway
  // (UPDATE ... WHERE abandon_reason IS NULL), but a double round-trip
  // is wasted.
  const reasonHandled = useRef(false);
  useEffect(() => {
    if (!reason || reasonHandled.current) return;
    reasonHandled.current = true;
    void recordReason({ data: { access_token: token, reason } }).catch((e) => {
      console.error("[session] reason capture failed", e);
    });
    void navigate({
      to: "/session/$token",
      params: { token },
      search: { reason: undefined },
      replace: true,
    });
  }, [reason, token, recordReason, navigate]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["pretest-session", token],
    queryFn: () => fetchSession({ data: { access_token: token } }),
    // While a payment is settling, a lost/late AzeriCard S2S callback would
    // otherwise strand the user on the Pay screen as if they never paid. Poll
    // gently so a late flip to `paid` surfaces without a re-pay; stops the
    // moment status is no longer pending_payment. Frontend-only — no change to
    // the payment engine or the signed callback path.
    refetchInterval: (query) =>
      query.state.data?.status === "pending_payment" ? 5000 : false,
  });
  // Экраны вступления и оплаты — на языке теста, даже если в адресе ?lang
  // (он только для отчёта).
  usePublishPageLanguage(data?.language_key ?? null);

  // Marketing `purchase` conversion — fired exactly once per browser the
  // first time a session is observed as genuinely paid.
  //   - amount_cents>0 gate: partner-comped sessions write amount_cents=0 /
  //     currency=null (see redeem_partner_code), so a free grant never counts
  //     as revenue. Preview / demo report routes never mount this layout.
  //   - paid | in_progress | complete all imply the money cleared server-side
  //     (the signed AzeriCard callback flips status→paid before the child can
  //     start), so any of the three is a valid "already paid" observation.
  //   - oncePerSession + transaction_id=session.id dedup across reloads and
  //     across devices (GA4 transaction_id / Meta eventID).
  useEffect(() => {
    if (!data) return;
    if (data.status !== "paid" && data.status !== "in_progress" && data.status !== "complete") {
      return;
    }
    if (!data.amount_cents || data.amount_cents <= 0) return;
    if (!oncePerSession(`purchase_${data.id}`)) return;
    trackPurchase({
      transactionId: data.id,
      value: data.amount_cents / 100,
      currency: data.currency ?? "AZN",
      tier: isKnownTier(data.package_type) ? data.package_type : "basic",
    });
  }, [data]);

  // Minimal loading
  if (isLoading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: P.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT,
          color: P.muted,
          fontSize: 15,
        }}
      >
        {T.ru.loading}
      </div>
    );
  }

  if (error || data === undefined) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: P.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT,
          color: P.muted,
          fontSize: 15,
        }}
      >
        {error instanceof Error ? error.message : T.ru.loading}
      </div>
    );
  }

  if (data === null) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: P.bg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT,
          color: P.muted,
          fontSize: 15,
          gap: 16,
        }}
      >
        <p>{T.ru.notFound}</p>
        <Link
          to="/start"
          search={{}}
          style={{ color: P.teal, fontWeight: 700, textDecoration: "none" }}
        >
          {T.ru.createNew}
        </Link>
      </div>
    );
  }

  // Payment pending — render the real AzeriCard kickoff form.
  // No client-side onPaid handler: the browser navigates away on submit,
  // and the eventual session.status flip to "paid" is driven server-side
  // by the AzeriCard callback handler (not by the frontend).
  if (data.status === "pending_payment") {
    return <PayScreen session={data} />;
  }

  // Complete → show report CTA
  if (data.status === "complete") {
    return <CompleteScreen session={data} token={token} reportLang={reportLang} />;
  }

  // Paid or in_progress → intro screen, preceded (for child bands) by
  // the parent-setup honesty screen ④.
  if (data.status === "paid" || data.status === "in_progress") {
    return (
      <IntroScreenWithParentSetup
        session={data}
        isContinue={data.status === "in_progress"}
        onStart={() => navigate({ to: "/session/$token/test", params: { token } })}
      />
    );
  }

  // Expired or unknown
  return (
    <div
      style={{
        minHeight: "100vh",
        background: P.bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: FONT,
        color: P.muted,
        fontSize: 15,
      }}
    >
      {data.status}
    </div>
  );
}
